<template>
  <div class="layout-widgets">
    <div class="d-flex justify-content-between align-items-center mb-3 position-relative" ref="configContainer">
      <div class="d-flex align-items-center">
        <i class="fal fa-calendar-alt mr-2 text-muted"></i>
        <date-picker
          v-model="fechaRango"
          type="date"
          range
          value-type="YYYY-MM-DD"
          format="DD/MM/YYYY"
          range-separator=" - "
          :editable="false"
          :clearable="false"
          style="width: 240px;"
        ></date-picker>
      </div>
      <div>
        <button class="btn btn-sm btn-info shadow-sm mr-2" @click="fetchDashboardStats" title="Actualizar datos" :disabled="loading">
          <i class="fas fa-sync-alt fa-sm text-white-50 mr-1" :class="{'fa-spin': loading}"></i> Actualizar
        </button>
        <button class="btn btn-sm btn-primary shadow-sm" @click="showConfig = !showConfig">
          <i class="fas fa-cog fa-sm text-white-50 mr-1"></i> Configurar Widgets
        </button>
      </div>

      <div v-if="showConfig" class="card shadow position-absolute" style="top: 100%; right: 0; z-index: 1000; width: 320px; margin-top: 0.5rem;">
        <div class="card-header py-3 d-flex flex-row align-items-center justify-content-between bg-white">
          <h6 class="m-0 font-weight-bold text-primary">Widgets Visibles</h6>
          <button type="button" class="close" @click="showConfig = false" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="card-body" style="max-height: 400px; overflow-y: auto;">

          <div class="input-group input-group-sm mb-3">
            <div class="input-group-prepend">
              <span class="input-group-text bg-white border-right-0"><i class="fal fa-search text-muted"></i></span>
            </div>
            <input
              type="text"
              class="form-control border-left-0"
              placeholder="Buscar widget..."
              v-model="widgetSearch"
              aria-label="Buscar widget"
            >
            <div class="input-group-append" v-if="widgetSearch">
              <button class="btn btn-outline-secondary border-left-0" type="button" @click="widgetSearch = ''" title="Limpiar">
                <i class="fal fa-times"></i>
              </button>
            </div>
          </div>

          <div class="custom-control custom-switch mb-3 pb-2 border-bottom">
            <input type="checkbox" class="custom-control-input" id="switch-all" v-model="allWidgetsVisible" :disabled="saving">
            <label class="custom-control-label small font-weight-bold" for="switch-all">Activar Todos</label>
          </div>

          <template v-for="group in groupedFilteredWidgets">
            <div :key="'group-' + group.groupKey" class="text-uppercase small font-weight-bold text-muted mt-3 mb-2 pb-1 border-bottom" style="letter-spacing: 0.05em;">
              {{ group.groupLabel }}
            </div>
            <div
              v-for="[key, config] in group.items"
              :key="'switch-' + key"
              class="custom-control custom-switch mb-2"
            >
              <input type="checkbox" class="custom-control-input" :id="'switch-' + key" v-model="config.visible" @change="handleSingleChange(key, config.visible)" :disabled="saving">
              <label class="custom-control-label small font-weight-bold" :for="'switch-' + key">{{ config.label }}</label>
            </div>
          </template>
          <div v-if="totalFilteredCount === 0" class="text-muted small text-center py-2">
            Sin coincidencias para "{{ widgetSearch }}"
          </div>
        </div>
      </div>
    </div>

    <template v-for="(groupLabel, groupKey) in widgetGroups">
      <template v-if="getVisibleTop5ByGroup(groupKey).length > 0">
        <div class="row mb-3" :key="'top5-header-' + groupKey">
          <div class="col-12">
            <h4>{{ groupLabel }}</h4>
          </div>
        </div>

        <div class="row" :key="'top5-row-' + groupKey">
          <div
            class="col-md-3 col-sm-6 mb-4"
            v-for="widget in getVisibleTop5ByGroup(groupKey)"
            :key="widget.key"
          >
            <WidgetTop5
              :title="widget.config.title"
              :subTitle="widget.config.subTitle"
              :items="Array.isArray(widget.data.total) ? widget.data.total : []"
              :groupTotal="widget.data.groupTotal"
              :variant="widget.config.variant"
              :loading="loading"
            />
          </div>
        </div>
      </template>
    </template>

    <b-row class="mb-3" v-if="filterKeys.length > 0">
      <b-col
        v-for="key in filterKeys"
        :key="key"
        cols="12"
        md="6"
        lg="3"
        class="mb-2"
      >
        <label class="small font-weight-bold mb-1">{{ activeFilters[key].label }}</label>
        <Select2
          :value="selectedFilters[key] || []"
          @input="setFilterValue(key, $event)"
          :options="filterDropdownOptions(key)"
          :multiple="true"
          :placeholder="activeFilters[key].placeholder"
        />
      </b-col>
    </b-row>

    <template v-for="(groupLabel, groupKey) in widgetGroups">
      <template v-if="getVisibleNumbersByGroup(groupKey).length > 0">
        <div class="row mb-3" :key="'num-header-' + groupKey">
          <div class="col-12">
            <h4>{{ groupLabel }}</h4>
          </div>
        </div>

        <div class="row" :key="'num-row-' + groupKey">
          <div
            class="col-md-3 col-sm-6 mb-4"
            v-for="widget in getVisibleNumbersByGroup(groupKey)"
            :key="widget.key"
          >
            <HomeWidget
              :title="widget.config.title"
              :subTitle="widget.config.subTitle"
              :value="widget.data.total"
              :variant="widget.config.variant"
              :to="widget.data.url"
              :loading="loading"
            />
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import HomeWidget from './Widget.vue';
import WidgetTop5 from './WidgetTop5.vue';
import Select2 from './Select2.vue';

export default {
  name: "LayoutWidgets",
  components: {
    HomeWidget,
    WidgetTop5,
    Select2,
  },
  data() {
    return {
      showConfig: false,
      loading: false,
      saving: false,
      widgetSettings: {},
      widgetSearch: '',
      apiData: {},
      fechaRango: [new Date().toISOString().split('T')[0], new Date().toISOString().split('T')[0]],
      activeFilters: {},
      selectedFilters: {},
    };
  },
  computed: {
    fechaInicio() {
      return Array.isArray(this.fechaRango) ? this.fechaRango[0] : null;
    },
    fechaTermino() {
      return Array.isArray(this.fechaRango) ? this.fechaRango[1] : null;
    },
    fechaFormateada() {
      const formatear = (f) => {
        if (!f) return null;
        const [y, m, d] = f.split('-');
        return `${d}/${m}/${y}`;
      };
      const inicio = formatear(this.fechaInicio);
      const termino = formatear(this.fechaTermino);
      if (!inicio && !termino) return 'hoy';
      if (inicio === termino) return inicio;
      return `${inicio} - ${termino}`;
    },
    widgetGroups() {
      return this.apiData.groups || {};
    },
    filteredWidgetSettings() {
      const term = (this.widgetSearch || '').trim().toLowerCase();
      if (!term) return this.widgetSettings;
      return Object.fromEntries(
        Object.entries(this.widgetSettings).filter(([, cfg]) => (cfg.label || '').toLowerCase().includes(term))
      );
    },
    groupedFilteredWidgets() {
      const groups = this.widgetGroups || {};
      const result = [];
      const seen = new Set();
      Object.keys(groups).forEach(groupKey => {
        const items = Object.entries(this.filteredWidgetSettings).filter(([, cfg]) => cfg.group === groupKey);
        if (items.length === 0) return;
        items.forEach(([key]) => seen.add(key));
        result.push({ groupKey, groupLabel: groups[groupKey], items });
      });
      const sinGrupo = Object.entries(this.filteredWidgetSettings).filter(([key]) => !seen.has(key));
      if (sinGrupo.length > 0) result.push({ groupKey: '__sin_grupo', groupLabel: 'Otros', items: sinGrupo });
      return result;
    },
    totalFilteredCount() {
      return this.groupedFilteredWidgets.reduce((acc, g) => acc + g.items.length, 0);
    },
    filterKeys() {
      return Object.keys(this.activeFilters);
    },
    filtersQueryString() {
      const params = new URLSearchParams();
      params.set('fecha_inicio', this.fechaInicio || '');
      params.set('fecha_termino', this.fechaTermino || '');
      Object.entries(this.activeFilters).forEach(([key, cfg]) => {
        const ids = this.selectedFilters[key] || [];
        ids.forEach((id) => {
          params.append(`${cfg.filter_param}[]`, id);
        });
      });
      return params.toString();
    },
    allWidgets() {
      if (!this.apiData || !this.apiData.widgets || Object.keys(this.apiData.widgets).length === 0) {
        return [];
      }

      return Object.keys(this.apiData.widgets).map(key => {
        const item = this.apiData.widgets[key];
        if (!item.title) return null;

        return {
          key: key,
          id: item.id,
          type: item.type,
          config: {
            title: item.title,
            subTitle: item.subTitle ? item.subTitle.replace(/hoy/gi, this.fechaFormateada) : item.subTitle,
            variant: item.variant,
            label: item.label || item.title,
            group: item.group,
            type: item.type
          },
          data: {
            total: item.data,
            groupTotal: item.group_total != null ? item.group_total : null,
            url: item.url ? `${item.url}?${this.filtersQueryString}` : null
          },
          visible: this.widgetSettings[key] ? this.widgetSettings[key].visible : false
        };
      }).filter(w => w !== null);
    },
    allWidgetsVisible: {
      get() {
        return this.allWidgets.length > 0 && this.allWidgets.every(w => w.visible);
      },
      set(value) {
        const newSettings = { ...this.widgetSettings };
        const ids = [];

        this.allWidgets.forEach(w => {
           if (!newSettings[w.key]) newSettings[w.key] = { label: w.config.label };
           newSettings[w.key].visible = value;
           if (w.id) ids.push(w.id);
        });
        this.widgetSettings = newSettings;

        if (ids.length > 0) {
            this.saveWidgetState(ids, value);
        }
      }
    }
  },
  watch: {
    widgetSettings: {
      handler(newSettings) {
        localStorage.setItem('elipse_widget_config', JSON.stringify(newSettings));
      },
      deep: true
    },
    fechaRango() {
      this.fetchDashboardStats();
    },
    selectedFilters: {
      handler() {
        this.fetchDashboardStats();
      },
      deep: true
    }
  },
  created() {
    const saved = localStorage.getItem('elipse_widget_config');
    if (saved) {
      try { this.widgetSettings = JSON.parse(saved); } catch(e) {}
    }
  },
  mounted() {
    this.fetchFilterOptions();
    this.fetchDashboardStats();
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    handleClickOutside(event) {
      if (this.showConfig && this.$refs.configContainer && !this.$refs.configContainer.contains(event.target)) {
        this.showConfig = false;
      }
    },
    saveWidgetState(ids, visible) {
      this.saving = true;
      const token = document.head.querySelector('meta[name="csrf-token"]');

      fetch('/admin/home/widgetsVisible', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': token ? token.content : ''
        },
        body: JSON.stringify({
          ids: ids,
          visible: visible
        })
      })
      .catch(error => {
        console.error('Error saving widget visibility:', error);
      })
      .finally(() => {
        this.saving = false;
      });
    },
    handleSingleChange(key, visible) {
        const widget = this.apiData.widgets[key];
        if (widget) {
            this.saveWidgetState([widget.id], visible);
        }
        // Re-fetch para asegurar que widgets recién activados tengan data fresca.
        if (visible) {
            this.fetchDashboardStats();
        }
    },
    getVisibleWidgetsByGroup(groupKey) {
      if (!this.allWidgets) return [];
      return this.allWidgets.filter(w => w.visible && w.config.group === groupKey);
    },
    getVisibleNumbersByGroup(groupKey) {
      return this.getVisibleWidgetsByGroup(groupKey).filter(w => w.config.type !== 'top5');
    },
    getVisibleTop5ByGroup(groupKey) {
      return this.getVisibleWidgetsByGroup(groupKey).filter(w => w.config.type === 'top5');
    },
    fetchFilterOptions() {
      fetch('/admin/home/widgetsFilters')
        .then(r => r.json())
        .then(data => {
          const filters = data.filters || {};
          this.activeFilters = filters;
          Object.keys(filters).forEach(key => {
            if (!Array.isArray(this.selectedFilters[key])) {
              this.$set(this.selectedFilters, key, []);
            }
          });
        })
        .catch(err => console.error('Error al cargar filtros del dashboard', err));
    },
    filterDropdownOptions(key) {
      const cfg = this.activeFilters[key];
      if (!cfg) return [];
      return (cfg.options || []).map(o => ({ value: o.id, text: o.nombre }));
    },
    setFilterValue(key, value) {
      this.$set(this.selectedFilters, key, Array.isArray(value) ? value : []);
    },
    fetchDashboardStats() {
      if (!this.fechaInicio || !this.fechaTermino) return;
      this.loading = true;
      fetch(`/admin/home/widgetsData?${this.filtersQueryString}`)
        .then(response => response.json())
        .then(data => {
          this.apiData = data;
          Object.keys(this.apiData.widgets).forEach(key => {
               const widgetInfo = this.apiData.widgets[key];
               if (!widgetInfo.label) return;
               if (!this.widgetSettings[key]) {
                   this.$set(this.widgetSettings, key, {
                       label: widgetInfo.label,
                       group: widgetInfo.group,
                       visible: widgetInfo.visible === true
                   });
               } else if (this.widgetSettings[key].group !== widgetInfo.group) {
                   this.$set(this.widgetSettings[key], 'group', widgetInfo.group);
               }
          });
        })
        .catch(error => {
          console.error('Error al obtener estadísticas del dashboard', error);
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }
};
</script>

